import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectMyFixing } from '_/hooks/useProjects';
import { formatRelativeTime } from '_/utils/dateUtils';
import { Table, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useEffect, useCallback } from 'react';

interface IMyFixingItem {
  id: string;
  collectionSlug: string;
  collectionName: string;
  sourceSlug: string;
  sourceName: string;
  stoppedAt: string;
}

function MyFixing(): ReactElement {
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();

  const { data: myfixing = [], isLoading, isRefetching, refetch } = useProjectMyFixing(selectedProject?.id);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, isRefetching, handleRefresh]);

  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'My Fixing' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug, selectedProject, selectedProjectSlug]);

  const columns: ColumnsType<IMyFixingItem> = [
    {
      title: 'Collection',
      dataIndex: 'collectionName',
      key: 'collectionName',
      render: (name: string, record: IMyFixingItem) => (
        <a href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}`}>{name}</a>
      ),
      sorter: (a: IMyFixingItem, b: IMyFixingItem) => a.collectionName.localeCompare(b.collectionName),
    },
    {
      title: 'Source',
      dataIndex: 'sourceName',
      key: 'sourceName',
      render: (name: string, record: IMyFixingItem) => (
        <a
          href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}`}
        >
          {name}
        </a>
      ),
      sorter: (a: IMyFixingItem, b: IMyFixingItem) => a.sourceName.localeCompare(b.sourceName),
    },
    {
      title: 'Lag',
      dataIndex: 'stoppedAt',
      key: 'stoppedAt',
      render: (stoppedAt: string) => formatRelativeTime(stoppedAt),
      sorter: (a: IMyFixingItem, b: IMyFixingItem) => new Date(a.stoppedAt).getTime() - new Date(b.stoppedAt).getTime(),
    },
    {
      title: '',
      key: 'actions',
      render: (_: any, record: IMyFixingItem) => (
        <a
          href={`/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${record.collectionSlug}/sources/${record.sourceSlug}/snapshots/${record.id}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button size="small">View</Button>
        </a>
      ),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <p>These are the snapshots that you are assigned to fix.</p>
      <Table
        dataSource={myfixing as IMyFixingItem[]}
        columns={columns}
        rowKey={(record: IMyFixingItem) => record.id}
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} items`,
        }}
      />
    </div>
  );
}

export default MyFixing;
